import { Injectable } from '@angular/core';
import get from 'lodash.get';
import { Attributes, Item, Product } from '~shared/models';
import { PriceTypes } from '~shared/modules/price-type-toggler/models/price-types.enum';
import { Category } from '~app/modules/generic/models';
import { ProductUtil } from '~shared/utils/product.util';

@Injectable({
  providedIn: 'root',
})
export class Ga4TrackingHelpersService {
  private static getCategoriesMapping(categories: Array<Category>, mustBeReversed = false): { [p: string]: string } {
    const list = mustBeReversed ? categories?.reverse() : categories;
    return list.reduce(
      (o, category, index) => ({
        ...o,
        [`item_category${index > 0 ? index + 1 : ''}`]: category.name,
      }),
      {}
    );
  }

  private static customItemAttrCount(attributes: Attributes): string {
    const attributesCoverage = attributes?.['custom_item_attributes_in_total'];
    return attributesCoverage ? attributesCoverage.value : '0';
  }

  static getGenericGA4ItemObject(product: Product, priceType: PriceTypes, index: number): Item {
    return {
      item_name: product.name,
      index: index + 1,
      item_id: product.id,
      item_offer_id: product.bestOffer?.id,
      price:
        priceType === PriceTypes.b2b
          ? get(product, 'bestOffer.price.netPrice', 0)
          : get(product, 'bestOffer.price.grossPrice', 0),
      item_brand: product?.attributes?.brand?.name,
      item_promo: product?.hasPromotion ? 'yes' : 'no',
      item_seller: product.bestOffer?.sellerName,
      item_discount_rate: product?.bestOffer?.referencePrice?.discountPercentage,
      item_variant: !!product?.variant?.types ? this.getItemVariant(product) : null,
      item_quantity: 1,
      item_energy_label: product?.attributes?.energyEfficiencyClass,
      item_volume_pricing: !!product?.bestOffer?.volumePrice?.length ? 'yes' : 'no',
      item_offer_type: 'best offer',
      ...this.getCategoriesMapping(product?.categories, true),
    };
  }

  static productsToEventMapper(product: Product, priceType: PriceTypes, index: number): Item {
    return {
      ...this.getGenericGA4ItemObject(product, priceType, index),
      ...this.getCategoriesMapping(product.categories, true),
    };
  }

  static productToSelectItemEventMapper(product: Product, priceType: PriceTypes, index: number): Item {
    return {
      ...this.getGenericGA4ItemObject(product, priceType, index),
      ...this.getCategoriesMapping(product.categories),
      item_placeholder_attribute: this.customItemAttrCount(product.attributes),
      item_strikethrough: product.bestOffer.referencePrice?.priceType ? 'yes' : 'no',
      item_other_offers: !!product.bestOffer && !!product.cheapestOffers ? 'yes' : 'no',
      item_recommendation: false,
    };
  }

  static getGA4ItemObject(product: Product, priceType: PriceTypes, index: number, colorVariantId?: string): Item {
    const categoriesList: Category[] = [...product?.categories].reverse() as any;
    const hasVariants = product?.variant?.types;
    let breadcrumbs: Category[] = get(product, 'categories', []);
    breadcrumbs = breadcrumbs?.slice()?.reverse();

    return {
      ...this.getGenericGA4ItemObject(product, priceType, index),
      item_name: product.name,
      item_id: colorVariantId ? colorVariantId : product.id,
      item_brand: product?.attributes?.brand?.name,
      ...(categoriesList.reduce((categories: {}, category: Category, categoryIndex: number) => {
        if (categoryIndex === 0) {
          categories['item_category'] = category.name;
        } else {
          categories[`item_category${categoryIndex + 1}`] = category.name;
        }

        return categories;
      }, {}) as any),
      item_variant: hasVariants ? this.getItemVariant(product, colorVariantId) : null,
      item_custom: hasVariants ? `default_id=${product.id}` : null,
      item_promo: product?.hasPromotion ? 'yes' : 'no',
      item_energy_label: product?.attributes?.energyEfficiencyClass,
      item_volume_pricing: !!product?.bestOffer?.volumePrice?.length ? 'yes' : 'no',
      item_offer_type: 'cheapest',
      item_discount_rate: product?.bestOffer?.referencePrice?.discountPercentage,
      item_recommendation: false,
      item_strikethrough: product?.bestOffer?.referencePrice?.priceType,
      item_black_deal_label: ProductUtil.isBlackDealProduct(product.tags) ? 'yes' : 'no',
      item_limited_stock: !!product.bestOffer && ProductUtil.isLimitedStock(product.bestOffer.quantity),
      item_offer_id: !!product.bestOffer && product.bestOffer.id,
      item_on_sale: !!product?.hasPromotion,
      category_tree: breadcrumbs?.map(({ id, name }) => ({ id, name })),
      index: index + 1,
      quantity: 1,
      price_type: priceType,
      price:
        priceType === PriceTypes.b2b
          ? get(product, 'bestOffer.price.netPrice', 0)
          : get(product, 'bestOffer.price.grossPrice', 0),
    };
  }

  private static getItemVariant(product: Product, colorVariantId?: string) {
    let mappedEntries;
    mappedEntries = Object.entries(product?.variant?.types)
      .filter(([key, { value }]) => value !== null)
      .map(([key, { value }]) => `${key}=${value}`);
    //color variant should not be the base variant//
    if (colorVariantId && colorVariantId !== product.id) {
      const colorVariant = product?.variant?.types?.color?.items?.find((item) => item.id === colorVariantId);
      mappedEntries = mappedEntries.map((item) => {
        if (item.includes('color')) {
          return `color=${colorVariant?.value}`;
        } else {
          return item;
        }
      });
    }
    return mappedEntries.join('&');
  }
}
